import { useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ComponentInfo } from "@/components/component-details/ComponentInfo";
import { DiagramsList } from "@/components/component-details/DiagramsList";
import { RelationsList } from "@/components/component-details/RelationsList";
import { ComponentDocumentation } from "@/components/documentation/ComponentDocumentation";
import { Timeline } from "@/components/component-details/Timeline";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";

export default function ComponentDetails() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const navigate = useNavigate();

  const { data: component } = useQuery({
    queryKey: ["component", id],
    queryFn: async () => {
      if (!id) return null;
      
      const { data, error } = await supabase
        .from("components")
        .select(`
          *,
          component_types(name, icon),
          organizations(name)
        `)
        .eq("id", id)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const { data: diagrams } = useQuery({
    queryKey: ["component-diagrams", id],
    queryFn: async () => {
      if (!id) return [];
      
      const { data: diagramComponents, error } = await supabase
        .from("diagram_components")
        .select("id_diagram, created_at")
        .eq("id_component", id);

      if (error) throw error;
      
      if (diagramComponents && diagramComponents.length > 0) {
        const diagramIds = diagramComponents.map(dc => dc.id_diagram);
        
        const { data: diagramsData, error: diagramsError } = await supabase
          .from("diagrams")
          .select("id, name")
          .in("id", diagramIds);
          
        if (diagramsError) throw diagramsError;
        
        return diagramComponents.map(dc => {
          const diagram = diagramsData?.find(d => d.id === dc.id_diagram);
          return {
            created_at: dc.created_at,
            diagram: {
              id: diagram?.id,
              name: diagram?.name
            }
          };
        });
      }
      
      return [];
    },
    enabled: !!id,
  });

  const { data: componentRelations, isLoading: isLoadingRelations } = useQuery({
    queryKey: ["component-relations", id],
    queryFn: async () => {
      if (!id) return [];
      
      try {
        // Buscar componentes relacionados com informações dos tipos
        const { data: allComponents, error: componentsError } = await supabase
          .from('components')
          .select(`
            id,
            name,
            component_types (
              icon
            ),
            organizations (
              name
            )
          `);
        
        if (componentsError) {
          console.error("Erro ao buscar componentes:", componentsError);
        }

        const { data: sourceRelations, error: sourceError } = await (supabase.rpc as any)(
          'get_component_source_relations',
          { component_id: id }
        );
        
        if (sourceError) {
          console.error("Erro ao buscar relações de origem:", sourceError);
          throw sourceError;
        }
        
        const { data: targetRelations, error: targetError } = await (supabase.rpc as any)(
          'get_component_target_relations',
          { component_id: id }
        );
        
        if (targetError) {
          console.error("Erro ao buscar relações de destino:", targetError);
          throw targetError;
        }
        
        const getComponentData = (componentId: string) => {
          return allComponents?.find(c => c.id === componentId);
        };
        
        const sourceFormattedRelations = sourceRelations ? sourceRelations.map((relation: any) => ({
          id: relation.id,
          source_id: relation.source_id,
          target_id: relation.target_id,
          source_name: component?.name || 'Este componente',
          source_component_type: component?.component_types,
          source_organization_name: (component as any)?.organizations?.name,
          target_name: relation.component_name || 'Componente relacionado',
          target_component: getComponentData(relation.target_id),
          created_at: relation.created_at
        })) : [];
        
        const targetFormattedRelations = targetRelations ? targetRelations.map((relation: any) => ({
          id: relation.id,
          source_id: relation.source_id,
          target_id: relation.target_id,
          source_name: relation.component_name || 'Componente relacionado',
          source_component: getComponentData(relation.source_id),
          target_name: component?.name || 'Este componente',
          target_component_type: component?.component_types,
          target_organization_name: (component as any)?.organizations?.name,
          created_at: relation.created_at
        })) : [];
        
        return [...sourceFormattedRelations, ...targetFormattedRelations];
      } catch (err) {
        console.error("Erro ao buscar relações:", err);
        return [];
      }
    },
    enabled: !!component && !!id,
  });

  const defaultTab = tabParam === 'documentation' ? 'documentation' : 
                    tabParam === 'timeline' ? 'timeline' : 'info';

  const handleBackToComponents = () => {
    navigate('/componentes');
  };

  return (
    <div className="container py-10 mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={handleBackToComponents}
            className="h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="space-y-0.5">
            <h2 className="text-3xl font-bold tracking-tight text-foreground">{component?.name || "Componente"}</h2>
            <p className="text-muted-foreground">
              Informações detalhadas sobre o componente e seus relacionamentos.
            </p>
          </div>
        </div>
      </div>

      <Tabs defaultValue={defaultTab} className="w-full" onValueChange={(value) => setSearchParams({ tab: value })}>
        <TabsList className="bg-card border border-border/40">
          <TabsTrigger value="info">Informações</TabsTrigger>
          <TabsTrigger value="documentation">Documentação</TabsTrigger>
          <TabsTrigger value="timeline">Linha do Tempo</TabsTrigger>
        </TabsList>
        <TabsContent value="info" className="space-y-6">
          <Card className="border border-border/40 shadow-sm">
            <CardContent>
              <ComponentInfo component={component} />
            </CardContent>
          </Card>
          <Card className="border border-border/40 shadow-sm">
            <CardContent>
              <DiagramsList diagrams={diagrams || []} />
            </CardContent>
          </Card>
          <Card className="border border-border/40 shadow-sm">
            <CardContent>
              <RelationsList 
                relations={componentRelations || []} 
                isLoading={isLoadingRelations}
              />
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="documentation">
          <Card className="border border-border/40 shadow-sm">
            <CardContent>
              {id && <ComponentDocumentation componentId={id} />}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="timeline">
          {id && <Timeline componentId={id} />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
